import React, { useMemo, useState } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import { toast } from "components/ui/use-toast";

import {
  useGetStoresOptionsWithDetailsQuery,
  type IStoreOptionWithDetails,
} from "../../../../../../services/store/select-options";
import { type IStore } from "../../../../../../@types/gamification";
import {
  STORE_OPTION_VALUES,
  STORE_CODE_VALUES,
} from "../../../constants/store-options";
import {
  formatStoreAddress,
  formatStoreNickname,
} from "../../../utils/store-formatters";
import { type CreateGamificationFormData } from "../../../validations/gamification.validation";
import { StoresCard } from "../StoresCard";

export const StepStores: React.FC = () => {
  const form = useFormContext<CreateGamificationFormData>();
  const {
    data: storeOptions = [],
    isLoading: isLoadingStores,
    isFetching: isFetchingStores,
  } = useGetStoresOptionsWithDetailsQuery();
  const [storeCodesInput, setStoreCodesInput] = useState("");

  const {
    fields: storeFields,
    append: appendStore,
    remove: removeStore,
  } = useFieldArray({ control: form.control, name: "stores" });

  const watchStores = useWatch({ control: form.control, name: "stores" });
  const allStoresSelected = useWatch({
    control: form.control,
    name: "allStoresSelected",
  });

  const selectedStoreCodes = (watchStores || [])
    .map((store) => String(store.storeCode))
    .filter(Boolean);

  const storeOptionsMap = useMemo(
    () =>
      new Map(
        storeOptions.map((store: IStoreOptionWithDetails) => [
          store.value,
          store,
        ]),
      ),
    [storeOptions],
  );

  const comboboxOptions = useMemo(
    () =>
      storeOptions.map((store: IStoreOptionWithDetails) => ({
        value: store.value,
        label: "Loja " + store.value,
      })),
    [storeOptions],
  );

  const filteredComboboxOptions = useMemo(
    () =>
      comboboxOptions.filter(
        (store) => !selectedStoreCodes.includes(String(store.value)),
      ),
    [comboboxOptions, selectedStoreCodes],
  );

  const storeOptionsWithAll = allStoresSelected
    ? comboboxOptions
    : [
        { value: STORE_OPTION_VALUES.ALL_STORES, label: "Todas as Lojas" },
        ...filteredComboboxOptions,
      ];

  const handleAddStore = (storeCode: string) => {
    if (storeCode === STORE_OPTION_VALUES.ALL_STORES) {
      form.setValue("stores", [
        {
          storeCode: STORE_CODE_VALUES.ALL_STORES_CODE,
          storeName: "",
          nickname: "",
        },
      ]);
      form.setValue("allStoresSelected", true);
      return;
    }

    const store = storeOptionsMap.get(storeCode);
    if (!store) return;

    if (form.getValues("allStoresSelected")) {
      form.setValue("stores", []);
      form.setValue("allStoresSelected", false);
    }

    const storeName =
      formatStoreAddress(storeCode, storeOptionsMap) || store.label;
    const nickname = formatStoreNickname(storeCode, storeOptionsMap);

    const newStore: IStore = { storeCode: store.value, storeName, nickname };
    appendStore(newStore);
  };

  const handleAddStoresByCodes = () => {
    if (!storeCodesInput.trim()) {
      return;
    }

    if (allStoresSelected) {
      form.setValue("stores", []);
      form.setValue("allStoresSelected", false);
    }

    const codes = Array.from(
      new Set(
        storeCodesInput
          .split(";")
          .map((code) => code.trim())
          .filter((code) => code.length > 0),
      ),
    );

    if (codes.length === 0) {
      toast({
        title: "Erro",
        description: "Nenhum código de loja válido foi informado",
        variant: "destructive",
      });
      return;
    }

    const currentStores = form.getValues("stores") || [];
    const existingCodes = currentStores.map((store) => String(store.storeCode));
    const newStores: IStore[] = [];
    const notFoundCodes: string[] = [];
    const duplicateCodes: string[] = [];

    codes.forEach((code) => {
      if (existingCodes.includes(String(code))) {
        duplicateCodes.push(code);
        return;
      }

      const store = storeOptionsMap.get(code) as
        | IStoreOptionWithDetails
        | undefined;

      if (store) {
        const storeName =
          formatStoreAddress(code, storeOptionsMap) || store.label;
        const nickname = formatStoreNickname(code, storeOptionsMap);
        newStores.push({ storeCode: store.value, storeName, nickname });
        existingCodes.push(String(code));
      } else {
        notFoundCodes.push(code);
      }
    });

    if (newStores.length > 0) {
      newStores.forEach((store) => appendStore(store));
      toast({
        title: "Sucesso",
        description: `${newStores.length} loja(s) adicionada(s) com sucesso`,
      });
    }

    if (duplicateCodes.length > 0) {
      const uniqueDuplicates = Array.from(new Set(duplicateCodes));
      toast({
        title: "Aviso",
        description: `Loja(s) ${uniqueDuplicates.join(", ")} já está(ão) na lista e não foi(ram) adicionada(s) novamente`,
        variant: "destructive",
      });
    }

    if (notFoundCodes.length > 0) {
      const uniqueNotFound = Array.from(new Set(notFoundCodes));
      uniqueNotFound.forEach((code) => {
        toast({
          title: "Aviso",
          description: `Loja ${code} não encontrada`,
          variant: "destructive",
        });
      });
    }

    setStoreCodesInput("");
  };

  const handleRemoveStore = (index: number) => {
    removeStore(index);
    if (storeFields.length === 1) {
      form.setValue("allStoresSelected", false);
    }
  };

  return (
    <StoresCard
      register={form.register}
      setValue={form.setValue}
      storeFields={storeFields}
      storeOptionsWithAll={storeOptionsWithAll}
      allStoresSelected={allStoresSelected || false}
      isLoadingStores={isLoadingStores || isFetchingStores}
      errors={form.formState.errors}
      onAddStore={handleAddStore}
      onRemoveStore={handleRemoveStore}
      storeCodesInput={storeCodesInput}
      setStoreCodesInput={setStoreCodesInput}
      onAddStoresByCodes={handleAddStoresByCodes}
      comboboxOptions={comboboxOptions}
    />
  );
};
